import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { filter, finalize, Observable } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthIntegrationTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { NotificationService } from '../notifications/notification.service';
import { GrowthAdminService, GrowthConnectorDto } from './growth-admin.service';
import {
  GROWTH_CONNECTOR_DIRECTIONS,
  GROWTH_CONNECTOR_KINDS,
  GROWTH_CONNECTOR_SCHEDULE_MODES,
  GROWTH_CONNECTOR_STATUSES,
  GROWTH_INTEGRATION_BUILTIN_AUTH_CONFIG_REFS,
  growthConnectorMappingFields,
  mergeConnectorMappingJson,
  normalizeConnectorMappingJson,
  toNotificationProviderAuthConfigRef
} from './growth-integration-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-integration-connector-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    StructuredJsonDisplayComponent,
    StructuredJsonFormComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="connector()?.displayName ?? t('growth.integration.connectorDetail.title')" [description]="t('growth.integration.connectorDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.integration.connectorDetail.help.title'"
        [descriptionKey]="'growth.integration.connectorDetail.help.description'"
        [bulletKeys]="['growth.integration.connectorDetail.help.bullets.mapping', 'growth.integration.connectorDetail.help.bullets.sync']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!connector()) {
        <ain-state type="empty" [title]="t('growth.integration.connectorDetail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/integration/connectors']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (editing()) {
              <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="cancelEdit()"></button>
              <button pButton type="button" [label]="t('common.save')" [loading]="actionType() === 'save'" (click)="save()"></button>
            } @else {
              <button pButton type="button" severity="secondary" [label]="t('common.edit')" (click)="startEdit()"></button>
              @if (canDisable()) {
                <button pButton type="button" severity="danger" [label]="t('growth.integration.connectorDetail.actions.disable')" [loading]="actionType() === 'disable'" (click)="disable()"></button>
              }
              @if (canOutboundSync()) {
                <button pButton type="button" [label]="t('growth.integration.connectorDetail.actions.outboundSync')" [loading]="actionType() === 'outboundSync'" (click)="outboundSync()"></button>
              }
            }
            <a class="link-btn" [routerLink]="['/growth/integration/sync-status']" [queryParams]="{ connectorKey: connectorKey }">{{ t('growth.integration.connectorDetail.openSyncStatus') }}</a>
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.integration.connectorDetail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.integration.connectorDetail.help.metadata.title'"
            [descriptionKey]="'growth.integration.connectorDetail.help.metadata.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.integration.connectors.fields.connectorKey') }}</span>
              <input pInputText type="text" [ngModel]="connector()!.connectorKey" [disabled]="true" />
            </label>
            @if (editing()) {
              <ain-form-field [label]="t('growth.integration.connectors.fields.displayName')">
                <input pInputText type="text" [(ngModel)]="editModel.displayName" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.direction')">
                <p-select [options]="directionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.direction" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.kind')">
                <p-select [options]="kindOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.kind" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.providerKey')">
                <input pInputText type="text" [(ngModel)]="editModel.providerKey" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.authConfigRef')">
                <p-select
                  [options]="authConfigOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="editModel.authConfigRef"
                  [showClear]="true"
                  [filter]="true"
                  appendTo="body"
                  [placeholder]="t('growth.integration.connectors.fields.authConfigRefPlaceholder')" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.status')">
                <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.status" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.integration.connectors.fields.scheduleMode')">
                <p-select [options]="scheduleModeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.scheduleMode" appendTo="body" />
              </ain-form-field>
            } @else {
              <label>
                <span>{{ t('growth.integration.connectors.fields.displayName') }}</span>
                <input pInputText type="text" [ngModel]="connector()!.displayName" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.direction') }}</span>
                <input pInputText type="text" [ngModel]="enumLabel('direction', connector()!.direction)" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.kind') }}</span>
                <input pInputText type="text" [ngModel]="enumLabel('kind', connector()!.kind)" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.providerKey') }}</span>
                <input pInputText type="text" [ngModel]="connector()!.providerKey" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.authConfigRef') }}</span>
                <input pInputText type="text" [ngModel]="authConfigLabel(connector()!.authConfigRef)" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.status') }}</span>
                <input pInputText type="text" [ngModel]="enumLabel('status', connector()!.status)" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.integration.connectors.fields.scheduleMode') }}</span>
                <input pInputText type="text" [ngModel]="enumLabel('scheduleMode', connector()!.scheduleMode)" [disabled]="true" />
              </label>
            }
            <label>
              <span>{{ t('growth.integration.connectors.fields.isSystemSeed') }}</span>
              <input pInputText type="text" [ngModel]="connector()!.isSystemSeed ? t('common.yes') : t('common.no')" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.integration.connectors.fields.webhookSecretMasked') }}</span>
              <input pInputText type="text" [ngModel]="connector()!.webhookSecretMasked || '-'" [disabled]="true" />
            </label>
          </div>
          @if (editing()) {
            <ain-form-field [label]="t('growth.integration.connectors.fields.webhookSecret')">
              <input pInputText type="password" [(ngModel)]="editModel.webhookSecret" [placeholder]="t('growth.integration.connectors.fields.webhookSecretRotatePlaceholder')" />
            </ain-form-field>
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.integration.connectorDetail.sections.mapping') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.integration.connectorDetail.help.mapping.title'"
            [descriptionKey]="'growth.integration.connectorDetail.help.mapping.description'" />
          @if (editing()) {
            <ain-structured-json-form [fields]="mappingFields" [(payloadJson)]="editModel.mappingJson" />
          } @else {
            <ain-structured-json-display [fields]="mappingFields" [payloadJson]="displayMappingJson()" />
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .link-btn { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthIntegrationConnectorDetailComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly notificationApi = inject(NotificationService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly connector = signal<GrowthConnectorDto | null>(null);
  readonly editing = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly actionType = signal<'save' | 'disable' | 'outboundSync' | null>(null);
  readonly mappingFields = growthConnectorMappingFields;
  readonly authConfigOptions = signal<Array<{ label: string; value: string }>>([]);

  connectorKey = '';
  editModel = {
    displayName: '',
    direction: 'Inbound',
    kind: 'External',
    providerKey: '',
    authConfigRef: null as string | null,
    status: 'Draft',
    scheduleMode: 'OnDemand',
    webhookSecret: '',
    mappingJson: '{}'
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.connectorKey = this.route.snapshot.paramMap.get('connectorKey') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthIntegrationChanged) {
            return false;
          }
          const payloadKey = event.payload['connectorKey'];
          return payloadKey === undefined || String(payloadKey).toLowerCase() === this.connectorKey.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadAuthConfigOptions();
    this.load(true);
  }

  private loadAuthConfigOptions(currentValue?: string | null): void {
    const builtin = GROWTH_INTEGRATION_BUILTIN_AUTH_CONFIG_REFS.map(value => ({
      value,
      label: this.t(`growth.integration.authConfig.${value}`)
    }));

    this.notificationApi.listProviderConfigs().subscribe({
      next: configs => {
        const providerOptions = configs
          .filter(item => item.isEnabled)
          .map(item => ({
            value: toNotificationProviderAuthConfigRef(item.id),
            label: `${item.displayName} (${item.providerKey}/${item.channel})`
          }));
        this.setAuthConfigOptions([...builtin, ...providerOptions], currentValue);
      },
      error: () => {
        this.setAuthConfigOptions(builtin, currentValue);
      }
    });
  }

  private setAuthConfigOptions(
    options: Array<{ label: string; value: string }>,
    currentValue?: string | null
  ): void {
    const next = [...options];
    if (currentValue && !next.some(item => item.value === currentValue)) {
      next.unshift({ value: currentValue, label: currentValue });
    }
    this.authConfigOptions.set(next);
  }

  authConfigLabel(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.authConfigOptions().find(item => item.value === value)?.label ?? value;
  }

  displayMappingJson(): string {
    return normalizeConnectorMappingJson(this.connector()?.mappingJson ?? '{}');
  }

  directionOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_DIRECTIONS.map(value => ({ value, label: this.enumLabel('direction', value) }));
  }

  kindOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_KINDS.map(value => ({ value, label: this.enumLabel('kind', value) }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_STATUSES.map(value => ({ value, label: this.enumLabel('status', value) }));
  }

  scheduleModeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_SCHEDULE_MODES.map(value => ({ value, label: this.enumLabel('scheduleMode', value) }));
  }

  enumLabel(group: 'direction' | 'kind' | 'status' | 'scheduleMode', value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.t(`growth.integration.enums.${group}.${value}`);
  }

  canDisable(): boolean {
    return this.connector()?.status === 'Active' && !this.connector()?.isSystemSeed;
  }

  canOutboundSync(): boolean {
    return this.connector()?.status === 'Active';
  }

  startEdit(): void {
    const row = this.connector();
    if (!row) {
      return;
    }
    this.loadAuthConfigOptions(row.authConfigRef);
    this.editModel = {
      displayName: row.displayName,
      direction: row.direction,
      kind: row.kind,
      providerKey: row.providerKey,
      authConfigRef: row.authConfigRef ?? null,
      status: row.status,
      scheduleMode: row.scheduleMode,
      webhookSecret: '',
      mappingJson: normalizeConnectorMappingJson(row.mappingJson)
    };
    this.editing.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  cancelEdit(): void {
    this.editing.set(false);
    this.errorMessage.set('');
  }

  save(): void {
    if (!this.connector()) {
      return;
    }
    this.runAction('save', () => this.api.upsertGrowthConnector({
      connectorKey: this.connectorKey,
      displayName: this.editModel.displayName.trim(),
      direction: this.editModel.direction,
      kind: this.editModel.kind,
      providerKey: this.editModel.providerKey.trim(),
      authConfigRef: this.editModel.authConfigRef?.trim() || null,
      mappingJson: mergeConnectorMappingJson(this.editModel.mappingJson),
      webhookSecret: this.editModel.webhookSecret.trim() || null,
      status: this.editModel.status,
      scheduleMode: this.editModel.scheduleMode
    }), 'saveFailed', () => {
      this.editing.set(false);
      this.successMessage.set(this.t('growth.integration.connectorDetail.messages.saveSucceeded'));
    });
  }

  disable(): void {
    this.runAction('disable', () => this.api.disableGrowthConnector(this.connectorKey), 'disableFailed', () => {
      this.successMessage.set(this.t('growth.integration.connectorDetail.messages.disableSucceeded'));
    });
  }

  outboundSync(): void {
    this.runAction('outboundSync', () => this.api.triggerGrowthConnectorOutboundSync(this.connectorKey), 'outboundSyncFailed', () => {
      this.successMessage.set(this.t('growth.integration.connectorDetail.messages.outboundSyncSucceeded'));
    });
  }

  load(showLoading = true): void {
    if (!this.connectorKey) {
      this.loading.set(false);
      this.connector.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getGrowthConnector(this.connectorKey).subscribe({
      next: row => {
        this.connector.set(row);
        this.loadAuthConfigOptions(row.authConfigRef);
        this.loading.set(false);
      },
      error: () => {
        this.connector.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.integration.connectorDetail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private runAction(
    type: 'save' | 'disable' | 'outboundSync',
    request: () => Observable<unknown>,
    errorKey: string,
    onSuccess?: () => void
  ): void {
    this.actionType.set(type);
    this.errorMessage.set('');
    this.successMessage.set('');
    request().pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        onSuccess?.();
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t(`growth.integration.connectorDetail.messages.${errorKey}`))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIntegrationTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
